import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { IconButton } from "./Button";

export interface DialogProps {
  open: boolean;
  title: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  onClose?: () => void;
  size?: "sm" | "md";
}

/** Modaler Dialog: Fokus hinein, Esc schließt, Fokus zurück; der Rest der Seite wird `inert`. */
export function Dialog({ open, title, children, actions, onClose, size = "md" }: DialogProps) {
  const ref = useRef<HTMLDivElement>(null);
  const vorher = useRef<Element | null>(null);

  useEffect(() => {
    if (!open) return;
    vorher.current = document.activeElement;
    const app = document.getElementById("app");
    app?.setAttribute("inert", "");
    const erstes = ref.current?.querySelector<HTMLElement>("button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])");
    (erstes ?? ref.current)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      app?.removeAttribute("inert");
      (vorher.current as HTMLElement | null)?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div className="rg-scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <div className={["rg-dialog", size === "sm" && "rg-dialog--sm"].filter(Boolean).join(" ")} role="dialog" aria-modal="true" aria-labelledby="rg-dialog-title" ref={ref} tabIndex={-1}>
        <div className="rg-dialog__head">
          <h2 className="rg-dialog__title" id="rg-dialog-title">
            {title}
          </h2>
          {onClose ? <IconButton icon="x" label="Schließen" onClick={onClose} /> : null}
        </div>
        <div className="rg-dialog__body">{children}</div>
        {actions ? <div className="rg-dialog__actions">{actions}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
